import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { LucideAngularModule, Map, Lightbulb, Search } from 'lucide-angular';
import { SelectTripModalService } from './select-trip-modal.service';
import { TripSelectionCardComponent } from './trip-selection-card.component';
import { TripForSelection } from './select-trip-modal.models';
import { TripCreationModalService } from 'src/app/trip-calculator/services/trip-creation-modal.service';

@Component({
  selector: 'app-select-trip-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ProgressSpinnerModule,
    LucideAngularModule,
    TripSelectionCardComponent,
  ],
  templateUrl: './select-trip-modal.component.html',
})
export class SelectTripModalComponent {
  protected modalService = inject(SelectTripModalService);
  private router = inject(Router);
  private tripCreationModalService = inject(TripCreationModalService);

  // Lucide icons
  mapIcon = Map;
  lightbulbIcon = Lightbulb;
  searchIcon = Search;

  get hasNoTrips(): boolean {
    return (
      !this.modalService.loading() &&
      this.modalService.tripsWithStatus().length === 0
    );
  }

  get hasNoResults(): boolean {
    return (
      !this.modalService.loading() &&
      this.modalService.tripsWithStatus().length > 0 &&
      this.modalService.filteredTrips().length === 0
    );
  }

  get infoMessage(): string {
    const stats = this.modalService.contributionStats();

    if (stats.contributed === 0) {
      return 'contributions.selectTripModal.infoFirst';
    }
    if (stats.contributed >= stats.total && stats.total > 0) {
      return 'contributions.selectTripModal.infoAllDone';
    }
    if (stats.drafts > 3) {
      return 'contributions.selectTripModal.infoDrafts';
    }
    return 'contributions.selectTripModal.infoContinue';
  }

  get infoMessageParams(): Record<string, number> {
    const stats = this.modalService.contributionStats();
    // Match count to the message being displayed
    if (stats.contributed === 0) {
      return { count: 0 }; // infoFirst doesn't use count
    }
    if (stats.contributed >= stats.total && stats.total > 0) {
      return { count: stats.contributed }; // infoAllDone doesn't use count
    }
    if (stats.drafts > 3) {
      return { count: stats.drafts }; // infoDrafts uses drafts count
    }
    return { count: stats.contributed }; // infoContinue uses contributed count
  }

  onSelectTrip(trip: TripForSelection): void {
    this.modalService.close();
    this.router.navigate(['/home/trips', trip.id, 'contribute']);
  }

  onFinalizeDraft(trip: TripForSelection): void {
    this.modalService.close();
    this.router.navigate(['/home/trips', trip.id, 'dashboard']);
  }

  onCreateTrip(): void {
    this.modalService.close();
    this.tripCreationModalService.createTrip();
  }

  onClose(): void {
    this.modalService.close();
  }
}
